import React, { useEffect, useRef, useState } from "react";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Sparkles, Wand2, Download, ArrowLeftRight, Save, Film, Camera } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BeforeAfter } from "./studio/BeforeAfter";
import { RunwayView } from "./studio/RunwayView";
import { PhotoMatchModal } from "./studio/PhotoMatchModal";
import { SaveOutfitDialog } from "./studio/SaveOutfitDialog";
import { UsageMeter, refreshUsage } from "./studio/UsageMeter";
import { IdentityCheck } from "./studio/IdentityCheck";

import { logger } from "../lib/log";
const POSES = [
  { id: "front", label: "Front" },
  { id: "side", label: "3/4 Side" },
  { id: "walking", label: "Walking" },
];

const BACKGROUNDS = [
  { id: "original", label: "Original Scene", hint: "Keep your photo's background" },
  { id: "studio", label: "Studio", hint: "Neutral seamless backdrop" },
  { id: "paris-street", label: "Paris Street", hint: "Golden-hour Haussmann" },
  { id: "tokyo-neon", label: "Tokyo Neon", hint: "Rainy Shibuya night" },
  { id: "beach-golden", label: "Beach Golden", hint: "White sand, warm rim" },
  { id: "mountain-lodge", label: "Mountain Lodge", hint: "Wood + fireplace" },
  { id: "desert-dunes", label: "Desert Dunes", hint: "Ochre sunset" },
  { id: "gallery", label: "Gallery", hint: "White cube museum" },
  { id: "rooftop-city", label: "Rooftop City", hint: "NYC blue hour" },
  { id: "botanical", label: "Botanical", hint: "Tropical greenhouse" },
  { id: "nordic-forest", label: "Nordic Forest", hint: "Misty pines" },
  { id: "custom", label: "Custom Scene", hint: "Describe it yourself" },
];

// Precomputed subset for the Runway scene picker (excludes "custom" which
// doesn't make sense for a batched multi-scene shoot, and "original" since
// runway's whole point is to test different scenes).
const RUNWAY_BACKGROUNDS = BACKGROUNDS.filter(
  (b) => b.id !== "custom" && b.id !== "original"
);

const OCCASION_IDEAS = [
  "First date, candlelit wine bar",
  "Sunday brunch in the West Village",
  "Gallery opening, minimal & dramatic",
  "Cocktail wedding, black tie optional",
  "Weekend hike, alpine cabin",
];

const Studio = () => {
  const [photos, setPhotos] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [instruction, setInstruction] = useState("");
  const [pose, setPose] = useState("front");
  const [background, setBackground] = useState("original");
  const [bgCustom, setBgCustom] = useState("");
  const [runwayMode, setRunwayMode] = useState(false);
  const [runwayScenes, setRunwayScenes] = useState(["studio", "gallery"]);
  const [runwayResult, setRunwayResult] = useState(null);
  const [runwayBusy, setRunwayBusy] = useState(false);
  const [runwayFrame, setRunwayFrame] = useState(0);
  const [includeOriginalRunway, setIncludeOriginalRunway] = useState(true);
  const [occasion, setOccasion] = useState("");
  const [missingPieces, setMissingPieces] = useState([]);
  const [wishlistCats, setWishlistCats] = useState([]);
  const [pmOpen, setPmOpen] = useState(false);
  const [pmImage, setPmImage] = useState(null); // data URL
  const [pmBusy, setPmBusy] = useState(false);
  const [pmResult, setPmResult] = useState(null);
  const pmFileRef = useRef(null);
  const [result, setResult] = useState(null);
  const [museNote, setMuseNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [museBusy, setMuseBusy] = useState(false);
  const [compare, setCompare] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [outfitName, setOutfitName] = useState("");
  const [publish, setPublish] = useState(false);
  const [savingOutfit, setSavingOutfit] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [p, w] = await Promise.all([api.get("/photos"), api.get("/wardrobe")]);
    setPhotos(p.data); setItems(w.data);
    if (p.data.length && !selectedPhoto) setSelectedPhoto(p.data[0].id);
    return w.data;
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Handle remix pre-selection from ?remix=slug OR prefilled occasion from ?occasion=
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    const remixSlug = p.get("remix");
    const preOccasion = p.get("occasion");
    if (preOccasion) {
      setOccasion(preOccasion);
      nav("/studio", { replace: true });
      return;
    }
    if (!remixSlug) return;
    (async () => {
      try {
        const r = await api.post(`/outfits/share/${remixSlug}/remix`);
        toast.success("Look remixed into your wardrobe");
        await load();
        setSelectedItems(r.data.item_ids.slice(0, 3));
        nav("/studio", { replace: true });
      } catch (err) {
        toast.error(errText(err, "Could not remix outfit"));
        nav("/studio", { replace: true });
      }
    })();
  }, [loc.search]); // eslint-disable-line

  const currentPhoto = photos.find((p) => p.id === selectedPhoto);

  const onUpload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = async () => {
      try {
        const r = await api.post("/photos", { data_url: rd.result, label: f.name });
        setPhotos((prev) => [r.data, ...prev]);
        setSelectedPhoto(r.data.id);
        toast.success("Portrait added");
      } catch { toast.error("Upload failed"); }
    };
    rd.readAsDataURL(f);
  };

  const toggleItem = (id) => {
    setSelectedItems((s) => s.includes(id) ? s.filter(x=>x!==id) : (s.length>=3 ? s : [...s, id]));
  };

  const runTryOn = async () => {
    if (!selectedPhoto) return toast.error("Choose a portrait first");
    if (!selectedItems.length) return toast.error("Pick 1–3 items");
    if (runwayMode) return runRunway();
    setBusy(true); setResult(null); setRunwayResult(null); setMuseNote("");
    try {
      const r = await api.post("/tryon", {
        photo_id: selectedPhoto, item_ids: selectedItems, instruction, pose,
        background, background_custom: background === "custom" ? bgCustom : null,
      });
      setResult(r.data);
      refreshUsage();
      toast.success("Fitting complete");
    } catch (e) {
      toast.error(errText(e, "AI try-on failed"));
    } finally { setBusy(false); }
  };

  const runRunway = async () => {
    const backgrounds = includeOriginalRunway ? ["original", ...runwayScenes] : runwayScenes;
    if (backgrounds.length < 2) return toast.error("Pick at least 2 scenes total");
    if (backgrounds.length > 3) return toast.error("Runway supports up to 3 scenes");
    setRunwayBusy(true); setResult(null); setRunwayResult(null); setMuseNote("");
    try {
      const r = await api.post("/runway", {
        photo_id: selectedPhoto, item_ids: selectedItems, pose,
        backgrounds, instruction,
      });
      setRunwayResult(r.data);
      setRunwayFrame(0);
      refreshUsage();
      toast.success(`Runway shot in ${r.data.frames.length} scenes`);
    } catch (e) {
      toast.error(errText(e, "Runway generation failed"));
    } finally { setRunwayBusy(false); }
  };

  const askMuse = async () => {
    if (!selectedPhoto) return toast.error("Choose a portrait first");
    if (!occasion.trim()) return toast.error("Tell Muse the occasion");
    if (items.length === 0) return toast.error("Add wardrobe items first");
    setMuseBusy(true); setResult(null); setMuseNote(""); setMissingPieces([]); setWishlistCats([]);
    try {
      const r = await api.post("/muse", { photo_id: selectedPhoto, occasion });
      setResult(r.data.tryon);
      setSelectedItems(r.data.selected_item_ids);
      setPose(r.data.pose);
      setBackground(r.data.background);
      setMuseNote(r.data.reasoning);
      setMissingPieces(r.data.missing_pieces || []);
      setWishlistCats(r.data.wishlist_categories || []);
      refreshUsage();
      toast.success("Muse composed a look");
    } catch (e) {
      toast.error(errText(e, "Muse could not compose a look"));
    } finally { setMuseBusy(false); }
  };

  const trackAdClick = (id) => {
    try { api.post(`/ads/${id}/click`).catch((e)=>logger.warn("[ad click]", e?.message)); } catch (e) { logger.warn("[ad click sync]", e); }
  };

  const onPmFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setPmImage(rd.result);
    rd.readAsDataURL(f);
  };

  const runPhotoMatch = async () => {
    if (!pmImage) return toast.error("Upload an inspiration photo first");
    setPmBusy(true); setPmResult(null);
    try {
      const r = await api.post("/muse/photo-match", { inspiration_data_url: pmImage, occasion });
      setPmResult(r.data);
      if (r.data.picked_item_ids?.length) {
        setSelectedItems(r.data.picked_item_ids.slice(0, 3));
      }
      toast.success("Muse decoded the look");
    } catch (e) {
      toast.error(errText(e, "Photo match failed"));
    } finally { setPmBusy(false); }
  };

  const closePm = () => { setPmOpen(false); setPmImage(null); setPmResult(null); };

  const openSave = () => {
    if (!result) return;
    setOutfitName(`Look ${new Date().toLocaleDateString(undefined,{month:"short",day:"2-digit"})}`);
    setPublish(false);
    setSaveOpen(true);
  };

  const doSaveOutfit = async (e) => {
    e.preventDefault();
    if (!outfitName.trim()) return;
    setSavingOutfit(true);
    try {
      const r = await api.post("/outfits", { name: outfitName, item_ids: selectedItems, tryon_id: result.id, is_public: publish });
      toast.success(publish ? "Published to Discover" : "Outfit saved");
      setSaveOpen(false);
      window.location.href = `/outfits/${r.data.share_slug}`;
    } catch (err) {
      toast.error(errText(err, "Could not save"));
    } finally { setSavingOutfit(false); }
  };

  const anyBusy = busy || museBusy || runwayBusy;
  const toggleScene = (id) => {
    setRunwayScenes((s) => {
      if (s.includes(id)) return s.filter(x => x !== id);
      const cap = includeOriginalRunway ? 2 : 3;
      return s.length >= cap ? s : [...s, id];
    });
  };

  return (
    <div className="max-w-[1500px] mx-auto px-6 sm:px-10 py-10">
      <div className="flex items-baseline justify-between border-b border-black/10 pb-6 mb-10">
        <div>
          <div className="font-mono-eyebrow">Fitting Room</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mt-2">The Studio</h1>
        </div>
        <div className="hidden md:flex items-center gap-4">
          <UsageMeter />
          <div className="font-mono-eyebrow text-black/50">Portrait × Garment × Scene = Portrait, revised</div>
        </div>
      </div>

      {/* MUSE STRIP (hero row) */}
      <div className="relative mb-10 border border-black/10 bg-gradient-to-br from-white to-[#F1EFE7] p-6 md:p-8 grain">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-full bg-black text-[#F6F5F1] flex items-center justify-center">
              <Wand2 className="w-4 h-4"/>
            </div>
            <div>
              <div className="font-mono-eyebrow">Muse · AI Stylist</div>
              <div className="font-serif-display text-2xl leading-none">Tell it the occasion.</div>
            </div>
          </div>
          <input
            value={occasion} onChange={(e)=>setOccasion(e.target.value)}
            data-testid="muse-occasion"
            placeholder="e.g. cocktail bar Friday night in Lisbon"
            className="field flex-1 !text-base"
            onKeyDown={(e)=>{ if(e.key==="Enter"){ e.preventDefault(); askMuse(); } }}
          />
          <button data-testid="muse-run-btn" onClick={askMuse} disabled={anyBusy}
            className="btn-ink whitespace-nowrap disabled:opacity-50 flex items-center gap-2">
            {museBusy ? <><Loader2 className="w-4 h-4 animate-spin"/> Composing…</> : <><Wand2 className="w-4 h-4"/> Ask Muse</>}
          </button>
          <button data-testid="photo-match-open" onClick={()=>setPmOpen(true)}
            title="Match a photo"
            className="btn-outline-ink whitespace-nowrap flex items-center gap-2">
            <Camera className="w-4 h-4"/> Match a photo
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 relative z-10">
          {OCCASION_IDEAS.map((o) => (
            <button key={o} onClick={()=>setOccasion(o)} data-testid={`muse-suggest-${o.slice(0,10)}`}
              className="text-xs px-3 py-1.5 border border-black/10 hover:border-black hover:bg-black hover:text-white transition-colors">{o}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8 lg:gap-10">
        {/* LEFT column: controls */}
        <div className="col-span-12 lg:col-span-5 space-y-8">
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-eyebrow">01 · Portrait</div>
              <button data-testid="upload-photo-btn" onClick={()=>fileRef.current?.click()} className="editorial-link font-mono-eyebrow flex items-center gap-2">
                <Upload className="w-3.5 h-3.5"/> Upload
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="photo-file-input"/>
            </div>
            {photos.length === 0 ? (
              <button onClick={()=>fileRef.current?.click()} className="w-full aspect-[4/5] border border-dashed border-black/30 flex flex-col items-center justify-center gap-3 hover:border-black transition-colors">
                <Upload className="w-6 h-6"/>
                <div className="font-mono-eyebrow">Add a full-body portrait</div>
              </button>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {photos.map(p => (
                  <button key={p.id} data-testid={`photo-thumb-${p.id}`}
                    onClick={()=>setSelectedPhoto(p.id)}
                    className={`aspect-[3/4] overflow-hidden relative ${selectedPhoto===p.id?"ring-2 ring-[color:var(--accent)]":"ring-1 ring-black/10"}`}>
                    <img src={p.data_url} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-eyebrow">02 · Items <span className="text-black/40 ml-2">({selectedItems.length}/3)</span></div>
              <Link to="/wardrobe" className="editorial-link font-mono-eyebrow">Add from URL →</Link>
            </div>
            {items.length === 0 ? (
              <div className="border border-dashed border-black/30 p-6 text-center">
                <p className="font-serif-display text-xl mb-2">Wardrobe is empty.</p>
                <Link to="/wardrobe" className="btn-outline-ink">Open wardrobe</Link>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {items.map(it => {
                  const on = selectedItems.includes(it.id);
                  return (
                    <button key={it.id} onClick={()=>toggleItem(it.id)}
                      data-testid={`item-select-${it.id}`}
                      className={`aspect-[3/4] overflow-hidden relative bg-white ${on?"ring-2 ring-[color:var(--accent)]":"ring-1 ring-black/10"}`}>
                      <img src={it.image_url} alt="" className="w-full h-full object-cover"/>
                      {on && <span className="absolute bottom-1 left-1 bg-[color:var(--accent)] text-white text-[9px] px-1.5 py-0.5 tracking-widest">ON</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section>
            <div className="font-mono-eyebrow mb-3">03 · Pose</div>
            <div className="flex gap-2 flex-wrap">
              {POSES.map(p => (
                <button key={p.id}
                  data-testid={`pose-${p.id}`}
                  onClick={()=>setPose(p.id)}
                  className={`px-4 py-2 border font-mono-eyebrow transition-colors ${pose===p.id?"bg-black text-[#F6F5F1] border-black":"border-black/20 hover:border-black"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-eyebrow">04 · Scene</div>
              <button
                data-testid="runway-toggle"
                onClick={()=>setRunwayMode(!runwayMode)}
                className={`text-xs px-3 py-1.5 border font-mono-eyebrow flex items-center gap-1.5 transition-colors ${runwayMode ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "border-black/20 hover:border-black"}`}>
                <Film className="w-3 h-3"/> Runway Triptych
              </button>
            </div>
            {!runwayMode ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  {BACKGROUNDS.map(b => (
                    <button key={b.id}
                      data-testid={`bg-${b.id}`}
                      onClick={()=>setBackground(b.id)}
                      className={`text-left p-3 border transition-colors ${background===b.id?"bg-black text-[#F6F5F1] border-black":"border-black/15 hover:border-black bg-white"}`}>
                      <div className="font-mono-eyebrow text-[10px]">{b.label}</div>
                      <div className={`text-[11px] mt-1 leading-tight ${background===b.id?"text-white/70":"text-black/50"}`}>{b.hint}</div>
                    </button>
                  ))}
                </div>
                {background === "custom" && (
                  <input value={bgCustom} onChange={(e)=>setBgCustom(e.target.value)}
                    data-testid="bg-custom-input"
                    placeholder="Describe the scene… e.g. neon-lit ramen shop at 2AM"
                    className="field mt-3"/>
                )}
              </>
            ) : (
              <>
                <label className="flex items-start gap-2 mb-3 p-2.5 border border-black/15 hover:border-black cursor-pointer transition-colors bg-white">
                  <input
                    type="checkbox"
                    data-testid="runway-include-original"
                    checked={includeOriginalRunway}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setIncludeOriginalRunway(on);
                      // If turning ON and already 3 scenes selected, drop the last to make room
                      if (on) setRunwayScenes((s) => s.slice(0, 2));
                    }}
                    className="mt-0.5 accent-black w-3.5 h-3.5"
                  />
                  <div className="flex-1">
                    <div className="font-mono-eyebrow text-[10px]">Include your original scene</div>
                    <div className="text-[11px] text-black/50 leading-tight mt-0.5">
                      Compare the look in your actual space vs a fantasy scene
                    </div>
                  </div>
                </label>
                <div className="text-xs text-black/60 leading-relaxed mb-3">
                  Same look, {(includeOriginalRunway ? 1 : 0) + runwayScenes.length} scene{(includeOriginalRunway ? 1 : 0) + runwayScenes.length === 1 ? "" : "s"} side-by-side — an editorial spread.
                  <span className="ml-1 text-[color:var(--accent)]">Uses {(includeOriginalRunway ? 1 : 0) + runwayScenes.length}× generation credits.</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {RUNWAY_BACKGROUNDS.map(b => {
                    const on = runwayScenes.includes(b.id);
                    const idx = runwayScenes.indexOf(b.id);
                    return (
                      <button key={b.id}
                        data-testid={`runway-scene-${b.id}`}
                        onClick={()=>toggleScene(b.id)}
                        className={`text-left p-3 border transition-colors relative ${on?"bg-black text-[#F6F5F1] border-black":"border-black/15 hover:border-black bg-white"}`}>
                        {on && <span className="absolute top-1.5 right-1.5 text-[10px] bg-[color:var(--accent)] text-white w-4 h-4 rounded-full flex items-center justify-center">{idx+1+(includeOriginalRunway?1:0)}</span>}
                        <div className="font-mono-eyebrow text-[10px]">{b.label}</div>
                        <div className={`text-[11px] mt-1 leading-tight ${on?"text-white/70":"text-black/50"}`}>{b.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          <section>
            <div className="font-mono-eyebrow mb-2">05 · Direction (optional)</div>
            <input value={instruction} onChange={(e)=>setInstruction(e.target.value)}
              data-testid="tryon-instruction"
              className="field" placeholder="e.g. shirt untucked, cinematic light"/>
          </section>

          <button data-testid="run-tryon-btn" onClick={runTryOn} disabled={anyBusy}
            className="btn-ink w-full flex items-center justify-center gap-3 disabled:opacity-50 !py-4">
            {busy || runwayBusy ? <><Loader2 className="w-4 h-4 animate-spin"/> {runwayBusy ? `Runway · ${(includeOriginalRunway?1:0)+runwayScenes.length} scenes…` : "Fitting in progress"}</> :
              runwayMode ? <><Film className="w-4 h-4"/> Shoot the runway</> : <><Sparkles className="w-4 h-4"/> Generate try-on</>}
          </button>
        </div>

        {/* RIGHT: result canvas */}
        <div className="col-span-12 lg:col-span-7">
          {runwayResult ? (
            <RunwayView
              runwayResult={runwayResult}
              runwayFrame={runwayFrame}
              setRunwayFrame={setRunwayFrame}
              sourcePhotoUrl={currentPhoto?.data_url}
            />
          ) : (
          <div className="relative bg-white border border-black/10 aspect-[3/4] overflow-hidden polaroid">
            {anyBusy && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#F6F5F1]/80 z-20">
                <div className="w-10 h-10 border-2 border-black border-t-transparent rounded-full animate-spin"/>
                <div className="font-mono-eyebrow">{runwayBusy ? "Shooting the runway…" : museBusy ? "Muse is composing…" : "The tailor is at work…"}</div>
                <div className="text-black/50 text-sm max-w-xs text-center">
                  {runwayBusy ? `Frame by frame — ${(includeOriginalRunway?1:0)+runwayScenes.length} scenes, one silhouette.` : museBusy ? "Reasoning across your wardrobe. Choosing pieces, pose, and scene." : "Fabric onto pixel. Scene into skin."}
                </div>
              </div>
            )}
            {!anyBusy && result && (
              compare && currentPhoto ? (
                <BeforeAfter data-testid="ba" before={currentPhoto.data_url} after={result.result_data_url} alt="try-on"/>
              ) : (
                <img data-testid="tryon-result" src={result.result_data_url} alt="try-on" className="w-full h-full object-cover"/>
              )
            )}
            {!anyBusy && !result && (
              <div className="w-full h-full flex flex-col items-center justify-center text-black/40 gap-4 p-8">
                <div className="font-serif-display text-5xl text-center">Awaiting a fitting</div>
                <div className="font-mono-eyebrow text-center">Ask Muse, or set portrait • items • scene manually</div>
              </div>
            )}
          </div>
          )}
          {result && currentPhoto && (
            <IdentityCheck beforeUrl={currentPhoto.data_url} afterUrl={result.result_data_url} />
          )}
          {museNote && result && (
            <div className="mt-3 border border-black/10 bg-[#F1EFE7] p-4 flex gap-3">
              <Wand2 className="w-4 h-4 mt-0.5 shrink-0"/>
              <div>
                <div className="font-mono-eyebrow mb-1">Muse's note</div>
                <div className="text-sm text-black/70 leading-relaxed" data-testid="muse-reasoning">{museNote}</div>
              </div>
            </div>
          )}
          {missingPieces.length > 0 && (
            <div className="mt-4 border-t border-black/10 pt-6" data-testid="muse-shopping">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="font-mono-eyebrow text-[color:var(--accent)]">Muse suggests · Shop the gap</div>
                  <h4 className="font-serif-display text-2xl leading-tight mt-1">
                    Your wardrobe is missing {wishlistCats.length ? wishlistCats.join(" + ") : "a piece"} — try these
                  </h4>
                </div>
                <span className="font-mono-eyebrow text-black/40">Affiliate</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {missingPieces.map(a => (
                  <a key={a.id} href={a.target_url} target="_blank" rel="noopener noreferrer sponsored"
                    data-testid={`muse-shop-${a.id}`}
                    onClick={()=>trackAdClick(a.id)}
                    className="group relative bg-white border border-black/10 block hover:border-black transition-colors">
                    <div className="aspect-[3/4] overflow-hidden">
                      <img src={a.image_url} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>
                    </div>
                    <span className="absolute top-2 left-2 bg-black/70 text-white text-[8px] uppercase tracking-widest px-1.5 py-0.5">Sponsored</span>
                    <div className="p-3">
                      <div className="font-mono-eyebrow text-black/40 text-[10px]">{a.brand}</div>
                      <div className="font-serif-display text-sm leading-tight line-clamp-2">{a.title}</div>
                      <div className="font-mono-eyebrow text-black/50 mt-1 capitalize text-[10px]">{a.category}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
          {result && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="font-mono-eyebrow">Look No. {result.id.slice(0,6)}</div>
                <button
                  data-testid="ba-toggle"
                  onClick={()=>setCompare((c)=>!c)}
                  className="editorial-link font-mono-eyebrow flex items-center gap-1">
                  <ArrowLeftRight className="w-3 h-3"/> {compare ? "Show final only" : "Compare before / after"}
                </button>
              </div>
              <div className="flex gap-2">
                <button data-testid="save-outfit-btn" onClick={openSave} className="btn-outline-ink flex items-center gap-2"><Save className="w-3.5 h-3.5"/> Save</button>
                <a data-testid="download-tryon" href={result.result_data_url} download={`atelier-${result.id}.png`} className="btn-ink flex items-center gap-2"><Download className="w-3.5 h-3.5"/> Download</a>
              </div>
            </div>
          )}
        </div>
      </div>

      <PhotoMatchModal
        open={pmOpen}
        onClose={closePm}
        pmImage={pmImage}
        pmBusy={pmBusy}
        pmResult={pmResult}
        pmFileRef={pmFileRef}
        onPmFile={onPmFile}
        onReplace={() => { setPmImage(null); setPmResult(null); }}
        runPhotoMatch={runPhotoMatch}
        onTryOnFromMatch={() => { setPmOpen(false); setTimeout(runTryOn, 200); }}
        trackAdClick={trackAdClick}
        selectedPhoto={selectedPhoto}
      />

      <SaveOutfitDialog
        open={saveOpen}
        outfitName={outfitName}
        setOutfitName={setOutfitName}
        publish={publish}
        setPublish={setPublish}
        savingOutfit={savingOutfit}
        onSubmit={doSaveOutfit}
        onClose={() => setSaveOpen(false)}
      />
    </div>
  );
};

export default Studio;
